import { apiToken } from '../../config/ApiService';
import axios from 'axios';
import type {
  NativeCommentThread,
  NativeDocument,
  NativeSuggestion,
} from './nativeEditorModel';

export type NativeParticipant = {
  email: string;
  name: string;
  type: 'blend' | 'guest';
  role: 'edit' | 'comment_suggest';
};

export type NativeEditorBootstrap = {
  sowId: string;
  document: NativeDocument;
  html: string;
  threads: NativeCommentThread[];
  suggestions: NativeSuggestion[];
  participants: NativeParticipant[];
  sourceVersion: number;
  sourceRevision?: string | null;
  revision: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export type NativeEditorStatePayload = {
  document: NativeDocument;
  html: string;
  threads: NativeCommentThread[];
  suggestions: NativeSuggestion[];
  expected_revision: number;
  source_version: number;
  source_revision?: string | null;
};

export type NativeGuestInvitation = {
  invitationId: number;
  email: string;
  name: string;
  role: 'comment_suggest';
  expiresOn?: string | null;
  revokedOn?: string | null;
  status: 'active' | 'expired' | 'revoked';
};

export const nativeEditorApi = {
  bootstrap: async (sowId: string, versionId?: string): Promise<NativeEditorBootstrap> => {
    const params = versionId ? { version: versionId } : undefined;
    const response = await apiToken.get<NativeEditorBootstrap>(
      `/pricing_tool/sows/${sowId}/native-editor/bootstrap`,
      { params },
    );
    return response.data;
  },

  saveState: async (sowId: string, payload: NativeEditorStatePayload): Promise<NativeEditorBootstrap> => {
    const response = await apiToken.put<NativeEditorBootstrap>(
      `/pricing_tool/sows/${sowId}/native-editor/state`,
      payload,
    );
    return response.data;
  },

  checkpoint: async (sowId: string, payload: NativeEditorStatePayload): Promise<NativeEditorBootstrap> => {
    const response = await apiToken.post<NativeEditorBootstrap>(
      `/pricing_tool/sows/${sowId}/native-editor/checkpoint`,
      payload,
    );
    return response.data;
  },

  reloadLatest: async (sowId: string, expectedRevision: number): Promise<NativeEditorBootstrap> => {
    const response = await apiToken.post<NativeEditorBootstrap>(
      `/pricing_tool/sows/${sowId}/native-editor/reload-latest`,
      undefined,
      { params: { expected_revision: expectedRevision } },
    );
    return response.data;
  },

  inviteGuest: async (sowId: string, input: { email: string; name?: string }) => {
    const response = await apiToken.post<{
      invitationId: number;
      email: string;
      inviteUrl: string;
      expiresInDays: number;
      developmentCode?: string;
    }>(`/pricing_tool/sows/${sowId}/native-editor/invitations`, input);
    return response.data;
  },

  listGuests: async (sowId: string): Promise<NativeGuestInvitation[]> => {
    const response = await apiToken.get<NativeGuestInvitation[]>(
      `/pricing_tool/sows/${sowId}/native-editor/invitations`,
    );
    return response.data;
  },

  revokeGuest: async (sowId: string, invitationId: number) => {
    const response = await apiToken.delete(
      `/pricing_tool/sows/${sowId}/native-editor/invitations/${invitationId}`,
    );
    return response.data;
  },
};

const nativeGuestClient = axios.create({
  baseURL: process.env.REACT_APP_SOW_STUDIO_API_URL || '/api/sow-studio',
  headers: { 'Content-Type': 'application/json' },
  timeout: 300000,
});

export type NativeGuestSession = {
  sessionToken: string;
  sowId: string;
  email: string;
  name: string;
  role: 'comment_suggest';
  expiresInHours: number;
};

export const nativeGuestApi = {
  verify: async (invitationToken: string, code: string): Promise<NativeGuestSession> => {
    const response = await nativeGuestClient.post<NativeGuestSession>('/pricing_tool/sow-native/guest/verify', {
      invitation_token: invitationToken,
      code,
    });
    return response.data;
  },

  exchangeMentionLink: async (token: string): Promise<NativeGuestSession> => {
    const response = await nativeGuestClient.post<NativeGuestSession>(
      '/pricing_tool/sow-native/guest/mention-session',
      undefined,
      { params: { token } },
    );
    return response.data;
  },

  bootstrap: async (sowId: string, sessionToken: string): Promise<NativeEditorBootstrap> => {
    const response = await nativeGuestClient.get<NativeEditorBootstrap>(
      `/pricing_tool/sow-native/guest/${sowId}/bootstrap`,
      { headers: { Authorization: `Bearer ${sessionToken}` } },
    );
    return response.data;
  },

  saveReview: async (
    sowId: string,
    sessionToken: string,
    payload: Pick<NativeEditorStatePayload, 'threads' | 'suggestions' | 'expected_revision'>,
  ): Promise<NativeEditorBootstrap> => {
    const response = await nativeGuestClient.put<NativeEditorBootstrap>(
      `/pricing_tool/sow-native/guest/${sowId}/review`,
      payload,
      { headers: { Authorization: `Bearer ${sessionToken}` } },
    );
    return response.data;
  },
};
